import { useSearchParams } from "react-router-dom";
import type { Lang, Tier } from "../../../../api/serving";

export interface LaunchParams {
  chapterId: string | null;
  tier: Tier;
  lang: Lang;
  role: "student" | "teacher";
  ltik: string | null;
  studentAssignmentId: string | null;
  /**
   * Serialized deployment selection ({ presetId, assets }) packed into the LTI
   * launch by the backend (custom.value query). Decoded once by useSearchParams;
   * null/absent ⇒ full CANONICAL_STEPS (today's default). See SceneReaderPage.
   */
  assets: string | null;
}

const TIERS: Tier[] = ["T1", "T2", "T3", "T4"];

/**
 * Reads the LTI launch context from the URL — the same per-assignment context
 * that `LtiResourcesPage` consumes (chapter_id / tier / lang / role / ltik).
 * `lang` is the existing per-student/per-assignment language setting.
 */
export function useLaunchParams(): LaunchParams {
  const [searchParams] = useSearchParams();

  const tierRaw = (searchParams.get("tier") ?? "").toUpperCase();
  const tier = (TIERS.includes(tierRaw as Tier) ? tierRaw : "T1") as Tier;

  const langRaw = (searchParams.get("lang") ?? "").toLowerCase();
  const lang = (langRaw === "es" ? "es" : "en") as Lang;

  // LTI roles arrive as URNs / comma lists; treat instructor/teacher as teacher.
  const roleRaw = (searchParams.get("role") ?? "").toLowerCase();
  const role = /instructor|teacher|faculty|staff|admin/.test(roleRaw)
    ? "teacher"
    : "student";

  return {
    chapterId: searchParams.get("chapter_id"),
    tier,
    lang,
    role,
    ltik: searchParams.get("ltik") ?? localStorage.getItem("ltiToken"),
    studentAssignmentId: searchParams.get("student_assignment_id"),
    assets: searchParams.get("assets"),
  };
}
